
import React, { useState, useMemo } from 'react';
import { Gavel, Coins, TrendingUp, TrendingDown, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { analyzeValueAuction } from '../services/geminiService';
import { ValueAuctionAnalysis } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  onClose: () => void;
}

const VALUES = [
    { id: 'wealth', label: 'Financial Wealth', desc: 'Luxury, security, accumulation' },
    { id: 'freedom', label: 'Creative Freedom', desc: 'Autonomy, expression, no boss' },
    { id: 'impact', label: 'Social Impact', desc: 'Helping others, changing the world' },
    { id: 'balance', label: 'Work-Life Balance', desc: 'Time for family, hobbies, rest' },
    { id: 'mastery', label: 'Professional Mastery', desc: 'Being the best, deep skill' },
    { id: 'status', label: 'Public Status', desc: 'Fame, recognition, prestige' },
    { id: 'stability', label: 'Job Security', desc: 'Predictability, low risk' },
    { id: 'challenge', label: 'Intellectual Challenge', desc: 'Solving hard problems, learning' },
];

const TOTAL_BUDGET = 1000;

export const ValueAuctionModal: React.FC<Props> = ({ onClose }) => {
  const [bids, setBids] = useState<Record<string, number>>(
      VALUES.reduce((acc, val) => ({ ...acc, [val.id]: 0 }), {})
  );
  const [step, setStep] = useState<'bid' | 'analyzing' | 'result'>('bid');
  const [analysis, setAnalysis] = useState<ValueAuctionAnalysis | null>(null);
  const { theme } = useTheme();

  const spent = useMemo(() => Object.values(bids).reduce((a: number, b: number) => a + b, 0), [bids]);
  const remaining = TOTAL_BUDGET - spent;

  const handleBidChange = (id: string, amount: number) => {
      if (amount < 0) return;
      const currentVal = bids[id];
      const diff = amount - currentVal;
      
      if (remaining - diff < 0) {
          const maxAdd = remaining;
          setBids(prev => ({ ...prev, [id]: prev[id] + maxAdd }));
      } else {
          setBids(prev => ({ ...prev, [id]: amount }));
      }
  };

  const handleSubmit = async () => {
      setStep('analyzing');
      try {
          const namedAllocation: Record<string, number> = {};
          Object.entries(bids).forEach(([key, val]) => {
              const label = VALUES.find(v => v.id === key)?.label || key;
              namedAllocation[label] = val as number;
          });

          const result = await analyzeValueAuction(namedAllocation);
          if (result) {
              setAnalysis(result);
              setStep('result');
          } else {
              setStep('bid');
          }
      } catch (e) {
          console.error(e);
          setStep('bid');
      }
  };

  return (
    <BaseModal
        onClose={onClose}
        title="The Value Auction"
        icon={<Gavel size={16} />}
        headerColor={theme.colors.features.auction}
        maxWidth="max-w-4xl"
    >
            {step === 'bid' && (
                <div className="space-y-6 p-6">
                    {/* Budget Tracker */}
                    <div className={`sticky top-0 z-10 backdrop-blur-sm p-4 rounded-xl border shadow-sm flex items-center justify-between mb-6 ${theme.mode === 'dark' ? 'bg-slate-800/90 border-slate-700' : 'bg-white/90 border-gray-100'}`}>
                        <div className="flex items-center gap-2">
                            <Coins className={remaining < 100 ? "text-red-500" : "text-amber-500"} size={24} />
                            <span className={`text-sm font-bold uppercase tracking-wide ${theme.colors.text.secondary}`}>Remaining Budget</span>
                        </div>
                        <div className={`text-3xl font-mono font-bold ${remaining < 0 ? 'text-red-600' : theme.colors.text.main}`}>
                            ${remaining}
                        </div>
                    </div>

                    <p className={`text-center max-w-lg mx-auto mb-8 ${theme.colors.text.secondary}`}>
                        You have <strong>$1,000</strong>. You cannot have it all. 
                        Bid high on your non-negotiables. Bid $0 on what you are willing to sacrifice.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {VALUES.map(val => (
                            <div key={val.id} className={`p-4 rounded-xl border transition-all ${
                                bids[val.id] > 0 
                                ? (theme.mode === 'dark' ? 'bg-amber-900/20 border-amber-900/40' : 'bg-amber-50 border-amber-200 shadow-sm')
                                : (theme.mode === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-100 opacity-80 hover:opacity-100')
                            }`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className={`font-bold ${theme.colors.text.main}`}>{val.label}</h3>
                                        <p className={`text-xs ${theme.colors.text.secondary}`}>{val.desc}</p>
                                    </div>
                                    <div className="text-xl font-mono font-bold text-amber-600">
                                        ${bids[val.id]}
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="1000" 
                                    step="10"
                                    value={bids[val.id]}
                                    onChange={(e) => handleBidChange(val.id, parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                                />
                            </div>
                        ))}
                    </div>

                    <div className={`pt-6 border-t flex justify-center ${theme.colors.divider}`}>
                         <button 
                            onClick={handleSubmit}
                            disabled={remaining > 0} 
                            className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all flex items-center gap-2 ${
                                remaining === 0 
                                ? 'bg-amber-600 hover:bg-amber-700 hover:scale-105' 
                                : 'bg-gray-300 cursor-not-allowed'
                            }`}
                         >
                            {remaining > 0 ? `Spend Remaining $${remaining}` : 'Close Auction & Analyze'} 
                            {remaining === 0 && <ArrowRight size={20} />}
                         </button>
                    </div>
                </div>
            )}

            {step === 'analyzing' && (
                <div className="flex flex-col items-center justify-center h-full py-20">
                     <Loader2 size={48} className="text-amber-500 animate-spin mb-4" />
                     <h3 className={`text-xl font-bold ${theme.colors.text.main}`}>Processing Bids...</h3>
                     <p className={`${theme.colors.text.secondary}`}>Kai is analyzing your trade-offs.</p>
                </div>
            )}

            {step === 'result' && analysis && (
                <div className="space-y-8 animate-scale-up p-6">
                    {/* Psychological Profile */}
                    <div className="bg-slate-800 text-white p-8 rounded-2xl shadow-xl text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500"></div>
                        <h3 className="text-2xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-orange-100">
                            {analysis.profile}
                        </h3>
                        <p className="text-slate-400 text-sm">Your Spending Profile</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Dominant Values */}
                        <div className={`rounded-xl p-6 border ${theme.colors.status.success}`}>
                             <h4 className="flex items-center gap-2 font-bold mb-4 text-green-700">
                                <TrendingUp size={20} /> Non-Negotiables
                             </h4>
                             <ul className="space-y-2">
                                {analysis.dominantValues.map((v, i) => (
                                    <li key={i} className={`flex items-center gap-2 font-medium p-2 rounded-lg ${theme.mode === 'dark' ? 'bg-green-900/40 text-green-200' : 'bg-white/50 text-green-900'}`}>
                                        <span className="w-6 h-6 rounded-full bg-green-200 text-green-700 flex items-center justify-center text-xs font-bold">{i+1}</span>
                                        {v}
                                    </li>
                                ))}
                             </ul>
                        </div>

                        {/* Sacrifices */}
                        <div className={`rounded-xl p-6 border ${theme.colors.status.error}`}>
                             <h4 className="flex items-center gap-2 font-bold mb-4 text-red-700">
                                <TrendingDown size={20} /> Sacrifices
                             </h4>
                             <ul className="space-y-2">
                                {analysis.sacrifices.map((v, i) => (
                                    <li key={i} className={`flex items-center gap-2 font-medium p-2 rounded-lg ${theme.mode === 'dark' ? 'bg-red-900/40 text-red-200' : 'bg-white/50 text-red-900'}`}>
                                        <span className="w-6 h-6 rounded-full bg-red-200 text-red-700 flex items-center justify-center text-xs font-bold">{i+1}</span>
                                        {v}
                                    </li>
                                ))}
                             </ul>
                        </div>
                    </div>

                    {/* Implications */}
                    <div className={`rounded-xl p-6 border ${theme.colors.status.info}`}>
                         <h4 className="flex items-center gap-2 font-bold mb-3 text-blue-700">
                            <AlertCircle size={20} /> Career Implications
                         </h4>
                         <p className={`leading-relaxed text-sm ${theme.mode === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                            {analysis.careerImplications}
                         </p>
                    </div>

                    <div className="flex justify-center pt-6">
                        <button 
                            onClick={() => {
                                setBids(VALUES.reduce((acc, val) => ({ ...acc, [val.id]: 0 }), {}));
                                setStep('bid');
                                setAnalysis(null);
                            }}
                            className={`${theme.colors.text.secondary} hover:text-amber-500 text-sm font-medium transition-colors`}
                        >
                            Restart Auction
                        </button>
                    </div>
                </div>
            )}
    </BaseModal>
  );
};
